import useSWR from "swr"

import { Datasets } from "../../../constants/enums"
import { useGetUserData } from "../../../hooks/useGetUserData"
import { canAccessProtectedRoutes } from "../../../utils/auth/authUtils"
import { getClientProtected, getClientPublic } from "../clients/clients"
import {
    ISingleDevice,
    singleDeviceMapper,
} from "../mappers/singleDeviceMapper"
import { SINGLE_DEVICE } from "../urls/hybrid"

export interface IUseMultiFetcherGetDevice {
    data: ISingleDevice | undefined
    isLoading: boolean
    isError: boolean
    mutate: () => void
    isValidating: boolean
}

/**
 * Hook for getting a single generation device. Checks authentication status
 * to determine if public / protected API should be used. Public users should
 * not be directed to the protected routes. If auth status is configuring,
 * the request should be skipped. Once authentication is complete the hook
 * will rerender, at which point an API request will be made.
 *
 * This hook has a conditional fetcher, which routes requests through
 * either the public or protected axios client based on auth status and role.
 * The request is deferred if the auth status is `isConfiguring`.
 * Therefore, the `isLoading` output value is augmented with this
 * parameter for this hook.
 *
 * @param {string} deviceId - the backend ID of the device to be returned
 * @param {Datasets} dataset - the type of dataset to be searched for `deviceId`
 * @returns {IUseMultiFetcherGetDevice} - SWR data, loading and error states.
 */
const useMultiFetcherGetDevice = (
    deviceId: string,
    dataset: Datasets
): IUseMultiFetcherGetDevice => {
    const { isSignedIn, isConfiguring, groups } = useGetUserData()
    const canAccessProtected = canAccessProtectedRoutes(isSignedIn, groups)
    const { data, error, isLoading, mutate, isValidating } = useSWR(
        isConfiguring
            ? null
            : SINGLE_DEVICE(deviceId, dataset, canAccessProtected),
        async (url: string): Promise<ISingleDevice> => {
            try {
                const fetcher = canAccessProtected
                    ? getClientProtected
                    : getClientPublic
                const res = await fetcher(url)
                return singleDeviceMapper.mapDevice(res, dataset)
            } catch {
                throw Error()
            }
        },
        { keepPreviousData: true }
    )
    return {
        data,
        isLoading: isLoading || isConfiguring, // See notes in docstring
        isError: error,
        mutate,
        isValidating,
    }
}

export default useMultiFetcherGetDevice
